import React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import {NextSyncOffset, SyncSource, SyncTarget} from '../types';

interface Props {
  items: SyncTarget[];
  loading: boolean;
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  nameFilter: string;
  sourceFilter: '' | SyncSource;
  enabledFilter: 'all' | 'enabled' | 'disabled';
  onChangeFilters: (filters: { name?: string; source?: '' | SyncSource; enabled?: 'all' | 'enabled' | 'disabled' }) => void;
  onChangePage: (newPage: number) => void;
  onChangePageSize: (newSize: number) => void;
  onToggleEnabled: (item: SyncTarget, newVal: boolean) => void;
  updating: Record<string, boolean>;
  onRowDoubleClick: (item: SyncTarget) => void;
  onPreview: (item: SyncTarget) => void;
  onDelete: (item: SyncTarget) => void;
}

export default function DataTableWithControls(props: Props) {
  const { items, loading, page, size, totalElements, nameFilter, sourceFilter, enabledFilter, onChangeFilters, onChangePage, onChangePageSize, onToggleEnabled, updating, onRowDoubleClick, onPreview, onDelete } = props;

  const formatDate = (iso?: string) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(d);
    } catch {
      return iso as string;
    }
  };

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ width: '100%', flexWrap: 'wrap' }} >
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel id="page-size-label">Page size</InputLabel>
          <Select
            labelId="page-size-label"
            label="Page size"
            value={String(size)}
            onChange={(e) => {
              const newSize = parseInt(String(e.target.value), 10);
              onChangePageSize(newSize);
            }}
          >
            {[5, 10, 20, 50].map((s) => (
              <MenuItem key={s} value={String(s)}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Name filter"
          value={nameFilter}
          onChange={(e) => onChangeFilters({ name: e.target.value })}
          placeholder="starts with..."
          sx={{ minWidth: 240 }}
        />
        {nameFilter && (
          <Button type="button" onClick={() => onChangeFilters({ name: '' })} disabled={loading} variant="outlined">
            Clear
          </Button>
        )}
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel id="source-filter-label">Source</InputLabel>
          <Select
            labelId="source-filter-label"
            label="Source"
            value={sourceFilter || ''}
            onChange={(e) => onChangeFilters({ source: e.target.value as any })}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Manual">Manual</MenuItem>
            <MenuItem value="Eurostat">Eurostat</MenuItem>
            <MenuItem value="ECB">ECB</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel id="enabled-filter-label">Enabled</InputLabel>
          <Select
            labelId="enabled-filter-label"
            label="Enabled"
            value={enabledFilter}
            onChange={(e) => onChangeFilters({ enabled: e.target.value as any })}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="enabled">Enabled</MenuItem>
            <MenuItem value="disabled">Disabled</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <TableContainer component={Paper} sx={{ overflowX: 'auto', width: '100%', maxWidth: '100%', display: 'block' }}>
        <Table size="small" sx={{ width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Enabled</TableCell>
              <TableCell>Next sync offset</TableCell>
              <TableCell>Next sync</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} /> Loading...
                  </Box>
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>No data</TableCell>
              </TableRow>
            ) : (
              items.map((it) => (
                <TableRow key={it.id} hover onDoubleClick={() => onRowDoubleClick(it)}>
                  <TableCell>{it.source}</TableCell>
                  <TableCell>{it.name}</TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="body2" noWrap title={it.description || ''}>{it.description || '-'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={!!it.enabled}
                      disabled={!!updating[it.id] || loading}
                      onChange={(e) => onToggleEnabled(it, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>{(it.nextSyncOffset as NextSyncOffset) || '-'}</TableCell>
                  <TableCell>{formatDate(it.nextSync)}</TableCell>
                  <TableCell>{formatDate(it.createdAt)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 200 }}>
                    <Button size="small" variant="outlined" onClick={() => onPreview(it)} sx={{ mr: 1 }}>Preview</Button>
                    <Button size="small" color="error" variant="outlined" onClick={() => onDelete(it)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalElements}
        page={page}
        onPageChange={(_, newPage) => onChangePage(newPage)}
        rowsPerPage={size}
        onRowsPerPageChange={(e) => onChangePageSize(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[5, 10, 20, 50]}
      />
    </>
  );
}
